import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/context/auth-context';
import { useQueryClient } from '@tanstack/react-query';

interface RedirectAuthProps {
  children: React.ReactNode;
  redirectTo: string;
  whenAuthenticated?: boolean;
}

/**
 * Component that redirects users based on authentication state
 * @param children Content to render if no redirect occurs
 * @param redirectTo Path to redirect to
 * @param whenAuthenticated If true, redirects when user is authenticated. If false, redirects when not authenticated
 */
export function RedirectAuth({
  children,
  redirectTo,
  whenAuthenticated = true
}: RedirectAuthProps) {
  const { user, isLoading, authToken } = useAuth();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    // Only redirect after auth state is determined
    if (!isLoading) {
      const shouldRedirect = whenAuthenticated ? !!user : !user;

      if (shouldRedirect) {
        setLocation(redirectTo);
      } else if (user && authToken) {
        // Clear any stale data in the query cache if the user logs in or changes
        queryClient.invalidateQueries();
      }
    }
  }, [user, isLoading, redirectTo, whenAuthenticated, setLocation, authToken, queryClient]);
  
  // While loading auth state or during redirect, show nothing
  if (isLoading || (whenAuthenticated ? !!user : !user)) {
    return null;
  }
  
  return <>{children}</>;
}